import { useParams } from 'react-router-dom';

import { Breadcrumb } from '@/components/common/Breadcrumb';
import { PageHeader } from '@/components/common/PageHeader';
import { QueryErrorView } from '@/components/common/QueryErrorView';
import { formatDate } from '@/features/common/format';
import { useGetOrganizationQuery } from '@/features/orgs/api/orgApi';
import { useGetProjectQuery } from '@/features/projects/api/projectsApi';

import { SubProjectsSection } from './SubProjectsSection';

/**
 * Full-page host for `SubProjectsSection` at
 * `/organizations/:orgId/projects/:projectId/reports`.
 *
 * Why this exists (design §6 places the section ON ProjectDetailPage): the
 * P3-T9 guard forbids editing files outside `features/reports/**`, and the
 * ProjectDetailPage embedding is a two-line host edit reported to the
 * coordinator as the integration blocker. Until it lands, this route keeps the
 * whole P3-T9 acceptance flow (attach → create environment → key once →
 * browse/search/download) reachable through the reports feature's own registry
 * subtree; afterwards the section lives on the project page (this page stays
 * as a deep-linkable duplicate — the rows link into the same detail tree).
 */
export function SubProjectsPage() {
  const { orgId = '', projectId = '' } = useParams<{ orgId: string; projectId: string }>();

  const projectQuery = useGetProjectQuery(projectId);
  const { data: organization } = useGetOrganizationQuery(orgId);

  // A denied/missing project must not keep its (empty) shell on screen — the
  // P1 ProjectDetailPage rule, mirrored.
  if (projectQuery.isError) {
    return (
      <QueryErrorView
        error={projectQuery.error}
        label="the project"
        onRetry={() => void projectQuery.refetch()}
      />
    );
  }

  return (
    <div>
      <Breadcrumb
        className="mb-3"
        items={[
          { label: 'Home', to: '/' },
          { label: 'Organizations', to: '/organizations' },
          { label: organization?.name ?? '…', to: `/organizations/${orgId}/projects` },
          { label: 'Projects', to: `/organizations/${orgId}/projects` },
          {
            label: projectQuery.data?.name ?? '…',
            to: `/organizations/${orgId}/projects/${projectId}`,
          },
          { label: 'Sub-projects', to: null },
        ]}
      />

      <PageHeader
        title={projectQuery.isLoading ? '…' : (projectQuery.data?.name ?? 'Project')}
        description={
          projectQuery.data?.description ?? `Created ${formatDate(projectQuery.data?.createdAt)}`
        }
      />

      <SubProjectsSection
        orgId={orgId}
        projectId={projectId}
        projectName={projectQuery.data?.name ?? 'this project'}
      />
    </div>
  );
}
